import type { FC, ReactNode } from 'react';
import React, { Profiler, useCallback, useEffect, useRef } from 'react';

import { fg } from '@confluence/feature-gating';
import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures';

import { sanitizeInputLoadablesForSSR } from './sanitizeInputLoadablesForSSR';

type FullCycleSSR = {
	active: boolean;
	htmlOutput: Record<string, string>;
};

export const FULLCYCLE_MEASURE_PREFIX = 'ssr-app/render/render-full-cycle';

declare global {
	interface Window {
		__FULLCYCLE_SSR__?: FullCycleSSR;
	}
}

function createFullCycleRenderRegistry() {
	const registry = new Set<Function>();

	function register(callback: Function) {
		registry.add(callback);
	}

	function unregister(callback: Function) {
		registry.delete(callback);
	}

	function notifyRenderComplete() {
		registry.forEach((callback) => callback());
	}

	return { register, unregister, notifyRenderComplete };
}

// This registry is used to keep track of all the components that need to be rendered in SSR during a full cycle render
export const fullCycleRenderRegistry = createFullCycleRenderRegistry();

// This component is used to facilite full cycle rendering in SSR for nested components.
// The intended usage is:
// - Wrap the component you want to render in SSR with this component
// - Pass a unique name prop to this component
// - The children of this component will be rendered in SSR during the full cycle render, and the output will be stored
// - Subsequent renders of the component during normal (non full-cycle) rendering will render the previously stored output
export const FullCycleRender: FC<{
	name: string;
	children: ReactNode;
}> = ({ name, children }) => {
	const fullCycleSSRState = window.__FULLCYCLE_SSR__;
	const containerRef = useRef<HTMLDivElement>(null);

	const onFullCycleRenderComplete = useCallback(() => {
		if (containerRef.current && fullCycleSSRState) {
			const html = sanitizeInputLoadablesForSSR(containerRef.current.innerHTML);
			fullCycleSSRState.htmlOutput[name] = html;
		}
	}, [name, fullCycleSSRState]);

	useEffect(() => {
		fullCycleRenderRegistry.register(onFullCycleRenderComplete);

		return () => fullCycleRenderRegistry.unregister(onFullCycleRenderComplete);
	}, [onFullCycleRenderComplete]);

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (!process.env.REACT_SSR || !fullCycleSSRState) {
		return null;
	}

	if (fullCycleSSRState.active) {
		return <div ref={containerRef}>{children}</div>;
	} else {
		const html = fullCycleSSRState.htmlOutput[name];

		return (
			// eslint-disable-next-line react/no-danger
			<div data-test-id="fullCycleOutput" dangerouslySetInnerHTML={{ __html: html }} />
		);
	}
};

// This component is required to ensure hydration of the DOM subtree that is rendered
// via FullCycleRender, as the wrapper is necessary on the server side.
export const FullCycleRenderWrapper: FC<{
	children: ReactNode;
}> = ({ children }) => {
	return <div data-test-id="fullCycleOutput">{children}</div>;
};

export const FullCycleRenderWithProfiler: FC<{
	name: string;
	children: ReactNode;
}> = ({ name, children }) => {
	const renders = useRef<Record<string, number>>({});

	const onRender = useCallback((id: string, phase: string, actualDuration: number) => {
		const updateName = `${id}/${phase}`;
		renders.current[updateName] = (renders.current[updateName] || 0) + 1;

		// We offset the start time by the duration of the previous render because we have to use internal "startTime"
		// to align with the rest of the waterfall. The startTime from React profiler is very different, so the best
		// approximation is to subtract the duration of the render from the SSRMeasures timestamp as onRender callback
		// is called after the render cycle.
		SSRMeasures.addMeasure(
			`${FULLCYCLE_MEASURE_PREFIX}/${updateName}/${renders.current[updateName]}`,
			actualDuration,
			true,
		);
	}, []);

	if (!fg('confluence_profile_fullcycle')) {
		return <FullCycleRender name={name}>{children}</FullCycleRender>;
	}

	return (
		<Profiler id={name} onRender={onRender}>
			<FullCycleRender name={name}>{children}</FullCycleRender>
		</Profiler>
	);
};
